import { createFileRoute, Link, useSearch } from "@tanstack/react-router";
import { CheckCircle2, Clock, LifeBuoy, Mail, XCircle } from "lucide-react";
import { useEffect, useState } from "react";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import { PageIntro } from "@/features/sales/storefront/components/PageIntro";
import { SiteFooter } from "@/features/sales/storefront/components/SiteFooter";
import { SiteHeader } from "@/features/sales/storefront/components/SiteHeader";
import { useCart } from "@/features/sales/storefront/cart/CartContext";
import { trackEventOnce } from "@/lib/analytics";
import { appCheckFetch } from "@/lib/firebase";

const searchSchema = z.object({
  status: z.enum(["approved", "pending", "rejected", "not_found", "invalid", "error"]).optional(),
  email: z.string().optional(),
  orderId: z.string().optional(),
  access: z.string().optional(),
});

export const Route = createFileRoute("/productos/gracias")({
  validateSearch: searchSchema,
  head: () => ({
    meta: [
      { title: "Confirmación de compra — Socrattica" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: ThanksPage,
});

type StatusKey = "approved" | "pending" | "rejected" | "not_found" | "invalid" | "error";

const STATE: Record<
  StatusKey,
  { icon: typeof CheckCircle2; title: string; description: string; note: string }
> = {
  approved: {
    icon: CheckCircle2,
    title: "¡Pago aprobado!",
    description: "Tu descarga privada ya está disponible en tu cuenta.",
    note: "Puedes volver cuando la necesites.",
  },
  pending: {
    icon: Clock,
    title: "Pago en proceso",
    description: "Te avisaremos por correo cuando se confirme.",
    note: "Estamos procesando tu pedido.",
  },
  rejected: {
    icon: XCircle,
    title: "Pago rechazado",
    description: "No pudimos procesar el pago. Puedes intentarlo nuevamente.",
    note: "Puedes volver a intentarlo.",
  },
  not_found: {
    icon: XCircle,
    title: "Pedido no encontrado",
    description: "No encontramos ese pedido. Revisa el enlace o consulta tus compras.",
    note: "Te ayudamos a encontrarlo.",
  },
  invalid: {
    icon: XCircle,
    title: "Enlace inválido",
    description: "El enlace no es válido. Por favor vuelve a intentarlo.",
    note: "Revisa el enlace recibido.",
  },
  error: {
    icon: XCircle,
    title: "Error de conexión",
    description: "Hubo un problema. Intenta actualizar la página en unos minutos.",
    note: "Inténtalo nuevamente.",
  },
};

function clearCheckoutIntent() {
  try {
    window.sessionStorage.removeItem("socrattica:active-checkout");
  } catch {
    // La confirmación funciona aunque el navegador bloquee sessionStorage.
  }
}

function ThanksPage() {
  const {
    status = "pending",
    email,
    orderId,
    access,
  } = useSearch({
    from: "/productos/gracias",
  });
  const { clear } = useCart();
  const [resolvedStatus, setResolvedStatus] = useState<StatusKey>(status as StatusKey);
  const state = STATE[resolvedStatus];
  const Icon = state.icon;

  useEffect(() => {
    if (!orderId || !access) return;
    const controller = new AbortController();
    let timer = 0;
    let attempts = 0;

    const checkStatus = async () => {
      attempts += 1;
      try {
        const params = new URLSearchParams({ orderId, access });
        const response = await appCheckFetch(`/api/epayco-order-status?${params}`, {
          signal: controller.signal,
          headers: { Accept: "application/json" },
        });
        const data = (await response.json().catch(() => null)) as {
          status?: "approved" | "pending" | "rejected";
          transactionId?: string;
          total?: number;
          currency?: string;
          items?: Array<{
            productId: string;
            title: string;
            quantity: number;
            unitPrice: number;
          }>;
        } | null;
        if (!response.ok || !data?.status) return;
        setResolvedStatus(data.status);
        if (data.status === "approved") {
          void trackEventOnce(`purchase:${orderId}`, "purchase", {
            transaction_id: data.transactionId ?? orderId,
            value: data.total ?? 0,
            currency: data.currency ?? "COP",
            items: (data.items ?? []).map((item) => ({
              item_id: item.productId,
              item_name: item.title,
              price: item.unitPrice,
              quantity: item.quantity,
            })),
          });
          clearCheckoutIntent();
          clear();
          return;
        }
        if (data.status === "rejected") {
          void trackEventOnce(`checkout-abandonment:${orderId}`, "checkout_abandonment", {
            transaction_id: data.transactionId ?? orderId,
            value: data.total ?? 0,
            currency: data.currency ?? "COP",
            items: (data.items ?? []).map((item) => ({
              item_id: item.productId,
              item_name: item.title,
              price: item.unitPrice,
              quantity: item.quantity,
            })),
          });
          clearCheckoutIntent();
        }
        if (data.status === "pending" && attempts < 15) {
          timer = window.setTimeout(checkStatus, 2_000);
        }
      } catch (error) {
        if (!(error instanceof DOMException && error.name === "AbortError") && attempts < 15) {
          timer = window.setTimeout(checkStatus, 2_000);
        }
      }
    };

    void checkStatus();
    return () => {
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [access, clear, orderId]);

  return (
    <div className="flex min-h-screen flex-col bg-white text-black">
      <SiteHeader />
      <main id="main-content" className="flex-1">
        <PageIntro
          eyebrow="Estado de la compra"
          title={state.title}
          description={state.description}
          aside={
            <div>
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-white text-black">
                <Icon className="h-6 w-6" />
              </span>
              <p className="mt-5 max-w-xs text-2xl font-semibold leading-tight tracking-[-0.04em]">
                {state.note}
              </p>
            </div>
          }
          details={["Pedido", "Correo", "Acceso"]}
        />

        <section className="mx-auto max-w-2xl px-4 py-12 text-center sm:px-6 sm:py-16">
          {(email || orderId) && (
            <div className="rounded-xl border border-black/15 bg-white p-5 text-left">
              {email && (
                <div className="flex items-start gap-3">
                  <Mail className="mt-0.5 h-5 w-5" />
                  <div>
                    <div className="text-xs font-semibold">Correo del pedido</div>
                    <div className="mt-1 text-xs text-black/50">{email}</div>
                  </div>
                </div>
              )}
              {email && orderId && <div className="my-4 border-t border-black/10" />}
              {orderId && (
                <div className="text-xs text-black/45">
                  Pedido:{" "}
                  <code className="rounded border border-black/10 px-1.5 py-0.5">{orderId}</code>
                </div>
              )}
            </div>
          )}

          <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row sm:flex-wrap">
            <Button asChild>
              <Link to="/productos/mis-compras">Ir a mis compras</Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/productos/tickets">
                <LifeBuoy />
                Contactar soporte
              </Link>
            </Button>
            <Button asChild variant="ghost">
              <Link to="/productos/catalogo">Seguir explorando</Link>
            </Button>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
